<template>    
    <ValidationObserver
        ref="costsplittingDialog"
        v-slot="{ passes }"
    >
        <BaseDialog
            :showDialog="showDialog"
            headline="lang_LABEL_RELEASES_COST_SPLITTING"
            maxDialogWidth="1500"
            maxCardHeight="1000"
            :close="close"
            persistent
            :alvqa="alvqa"
        >
            <template v-slot:dialogText>
                <v-container fluid>
                    <v-row>
                        <v-col class="pl-4" cols="2">
                            <BaseEditPageDatePicker 
                                :context="context"
                                field="Date"
                                rules="required"
                                :label="$t('lang_GENERIC_CAPTION_DATE')"
                                :alvqa="alvqa + '-datepicker-date'"
                                :withMargin="false"
                            />
                        </v-col>
                        <v-col class="pl-0" cols="2">
                            <BaseEditPageAutocomplete 
                                :context="context"
                                field="Recipient"
                                :label="$t('lang_GENERIC_CAPTION_RECIPIENT')"
                                rules="required"
                                :withFreeText="false"
                                :withMargin="false"
                                :alvqa="alvqa + '-autocomplete-recipient-textfield'"
                            >
                                <template v-slot:selection="{ item }">
                                    <div class="ellipsis" :alvqa="alvqa + '-autocomplete-recipient-selection'">
                                        {{ item.gemaName }}
                                        <span v-if="$alvPlugin.printName(item)">
                                            ({{ item.firstName }} {{ item.lastName }})
                                        </span>
                                    </div>
                                </template>
                                <template v-slot:items="{ items }">
                                    <v-list-item-content :alvqa="alvqa + '-autocomplete-recipient-dropdown-' + items.alvId">
                                        <v-list-item-title :alvqa="alvqa + '-autocomplete-recipient-dropdown-' + items.alvId + '-title'">
                                            {{ items.gemaName }}
                                            {{ $alvPlugin.printName(items) ? '(' + items.firstName + ' ' + items.lastName +')' : ''}}
                                            <v-avatar color="grey" size="20" class="caption">{{ items.typeOfAz }}</v-avatar>
                                        </v-list-item-title>
                                        <v-list-item-subtitle :alvqa="alvqa + '-autocomplete-recipient-dropdown-' + items.alvId + '-subtitle'">
                                            {{ $t('lang_IPI') }}: {{ items.ipi !== '' ? items.ipi : $t('lang_GENERIC_CAPTION_NONE') }} | {{ items.alvId }}
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>
                            </BaseEditPageAutocomplete>
                        </v-col>
                        <v-col class="pl-0" cols="2">
                            <BaseEditPageTextField
                                :context="context"
                                field="DocumentNumber"
                                :label="$t('lang_LABEL_RELEASES_CAPTION_DOCUMENT_NUMBER')" 
                                :alvqa="alvqa + '-textfield-document-number'"
                                :withMargin="false"
                            />
                        </v-col>
                        <v-col class="pl-0" cols="2">
                            <BaseEditPageTextField
                                :context="context"
                                field="Amount"
                                :label="$t('lang_GENERIC_CAPTION_AMOUNT')" 
                                :alvqa="alvqa + '-textfield-document-number'"
                                rules="required|amountMaxTwoDigits"
                                :clearable="false"
                                :withMargin="false"
                                :additionalFunction="calculateWithNewAmount"
                            />
                        </v-col>
                        <v-col cols="2" class="pr-3 pl-0">
                            <BaseEditPageDropdown 
                                :context="context"
                                field="Country"
                                :items="countryCodeItems" 
                                :label="$t('lang_GENERAL_CAPTION_COUNTRY')" 
                                :alvqa="alvqa + '-dropdown-country'"
                                :withMargin="false"
                            />
                        </v-col>
                        <v-col class="pl-0" cols="2">
                            <BaseEditPageDropdown 
                                :context="context"
                                field="CostGroup"
                                :items="costGroupItems" 
                                :label="$t('lang_LABEL_RELEASES_CAPTION_COST_GROUP')" 
                                :alvqa="alvqa + '-dropdown-cost-group'"
                                :clearable="false"
                                :withMargin="false"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col class="pl-4" cols="4">
                            <BaseEditPageAutocomplete 
                                :context="context"
                                field="Release"
                                rules="required"
                                :label="$t('lang_LABEL_RELEASES_CAPTION_CATALOGUE_NUMBER') + '/EAN'"
                                :withFreeText="false"
                                :withMargin="false"
                                :alvqa="alvqa + '-autocomplete-release-textfield'"
                            >
                                <template v-slot:selection="{ item }">
                                    <div class="ellipsis" :alvqa="alvqa + '-autocomplete-release-selection'">
                                        {{ item.name }} ({{ item.alvId }})
                                    </div>
                                </template>
                                <template v-slot:items="{ items }">
                                    <v-list-item-content :alvqa="alvqa + '-autocomplete-release-dropdown-' + items.alvId">
                                        <v-list-item-title :alvqa="alvqa + '-autocomplete-release-dropdown-' + items.alvId + '-title'">
                                            {{ items.name }} ({{ items.alvId }})
                                        </v-list-item-title>
                                    </v-list-item-content> 
                                </template>
                            </BaseEditPageAutocomplete>
                        </v-col>
                        <v-col class="pl-0" cols="4">
                            <BaseEditPageTextField
                                :context="context"
                                field="Description"
                                :label="$t('lang_LABEL_RELEASES_CAPTION_DESCRIPTION_OF_COST_ITEM')" 
                                :alvqa="alvqa + '-textfield-description'"
                                :withMargin="false"
                            />
                        </v-col>
                        <v-col cols="2" class="pl-0">
                            <BaseEditPageTextField
                                :context="context"
                                field="Share"
                                :label="$t('lang_LABEL_RELEASES_CAPTION_SHARE') + ' %'" 
                                :alvqa="alvqa + '-textfield-share-percentage'"
                                rules="percentDecimal"
                                :clearable="false"
                                :withMargin="false"
                            />
                        </v-col>
                        <v-spacer></v-spacer>
                        <v-col 
                            class="pl-0"
                            align-self="center"
                            cols="2"
                        >
                            <BaseButton 
                                @click="addNewRow"
                                label="lang_GENERAL_BUTTON_ADD_ROW"
                                block
                                :alvqa="alvqa + '-button-add-row'"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col>
                            <v-data-table
                                :headers="headers"
                                :items="releaseCostsplittingRows"
                                hide-default-footer
                                :header-props="{ sortIcon: '' }"
                                :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                                :items-per-page="100"
                            >
                                <template v-slot:[`item.release`]="{ item }">
                                    <div :alvqa="alvqa + '-list-row-text-catalogue-nr-ean'">
                                        {{ 
                                            item.release.catalogueNumber !== null ? item.release.catalogueNumber : 
                                            item.release.ean !== null ? item.release.ean : 
                                            item.release.name + '(' + item.release.alvId + ')'
                                        }}
                                    </div>
                                </template>
                                <template v-slot:[`item.share`]="{ item }">
                                    <div 
                                        class="text--right"
                                        :alvqa="alvqa + '-list-row-text-share'"
                                    >
                                        {{ $n(item.share, 'number') }}
                                    </div>
                                </template>
                                <template v-slot:[`item.proRataAmount`]="{ item }">
                                    <div 
                                        class="text--right"
                                        :alvqa="alvqa + '-list-row-text-pro-rata-amount'"
                                    >
                                        {{ $n(item.proRataAmount, 'number') }}
                                    </div>
                                </template>
                                <template v-slot:[`item.delete`]="{ item }">
                                    <BaseIcon 
                                        @click="deleteRow(item)"
                                        icon="delete"
                                        color="iconDelete"
                                        tooltipText="lang_GENERIC_CAPTION_DELETE"
                                        :alvqa="alvqa + 'list-row-icon-delete'"
                                    />
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </v-container>
            </template>
            <template v-slot:dialogActions>
                <v-container fluid>
                    <v-row>
                        <v-spacer />
                        <v-col 
                            v-if="errorMessage !== null"
                            cols="10"
                            style="color:brown;"
                            class="text-right"
                            align-self="center"
                        >
                            {{ $t(errorMessage) }}
                        </v-col>
                        <v-col class="text-right">
                            <BaseButton 
                                label="lang_GENERAL_BUTTON_SAVE"
                                @click="passes(splittingCosts)"
                                :alvqa="alvqa + '-button-save'"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </template>
        </BaseDialog>
    </ValidationObserver>
</template>

<script>
import { DropdownGenericCostGroupTypes } from '@/assets/dropdowns/alvDropdowns.js'

import BaseDialog from '@/components/_base/BaseDialog'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseIcon from '@/components/_buttons/BaseIcon'

import BaseEditPageDatePicker from '@/components/_editForm/BaseEditPageDatePicker'
import BaseEditPageAutocomplete from '@/components/_editForm/BaseEditPageAutocomplete'
import BaseEditPageTextField from '@/components/_editForm/BaseEditPageTextField'
import BaseEditPageDropdown from '@/components/_editForm/BaseEditPageDropdown'

export default {
    name: "LabelReleasesCostsplittingDialog",
    components: {
        BaseDialog,
        BaseButton,
        BaseIcon,
        BaseEditPageDatePicker,
        BaseEditPageAutocomplete,
        BaseEditPageTextField,
        BaseEditPageDropdown
    },
    data() {
        return {
            alvqa: 'label-releases-edit-form-costsplitting-dialog',
            context: 'LabelReleasesCostsplitting',
            errorMessage: null
        }
    },
    computed: {
        headers() {
            return [
                {
                    text: this.$t("lang_LABEL_RELEASES_CAPTION_CATALOGUE_NUMBER") + '/EAN', 
                    align: "left", 
                    value: "release",
                    class: 'text-no-wrap',
                    alvqa: this.alvqa + '-header-catalogue-nr-ean',
                    sortable: false,
                },
                {
                    text: this.$t("lang_LABEL_RELEASES_CAPTION_DESCRIPTION_OF_COST_ITEM"), 
                    align: "left", 
                    value: "description",
                    class: 'text-no-wrap',
                    width: '450px',
                    alvqa: this.alvqa + '-header-description',
                    sortable: false,
                },
                {
                    text: this.$t("lang_LABEL_RELEASES_CAPTION_SHARE") + ' %', 
                    align: "right", 
                    value: "share",
                    class: 'text-no-wrap',
                    alvqa: this.alvqa + '-header-share',
                    sortable: false,
                },
                {
                    text: this.$t("lang_LABEL_RELEASES_CAPTION_PRO_RATA") + ' ' + this.$store.getters.getSelectedCurrency, 
                    align: "right", 
                    value: "proRataAmount",
                    class: 'text-no-wrap',
                    alvqa: this.alvqa + '-header-pro-rata-amount',
                    sortable: false,
                },
                {
                    text: "",
                    align: "right",
                    width: "70px",
                    value: "delete",
                    sortable: false,
                    class: 'text-no-wrap'
                }
            ]
        },
        showDialog: {
            get() { return this.$store.getters.getShowReleaseCostsplittingDialog },
            set(show) { this.$store.commit('setShowReleaseCostsplittingDialog', show) }
        },
        releaseCostsplittingRows: {
            get() { return this.$store.getters.getReleaseCostsplittingRows },
            set(item) { return this.$store.commit('setReleaseCostsplittingRows', item) }
        },
        costGroupItems() {
            let costGroup = JSON.parse(JSON.stringify(DropdownGenericCostGroupTypes))
            costGroup.forEach(element => {
                element.text = this.$t(element.text)
            });
            costGroup.sort((a,b) => (a.text > b.text) ? 1 : (b.text > a.text) ? -1 : 0)
            return costGroup
        },
        countryCodeItems() {
            let items = JSON.parse(JSON.stringify(this.$alvPlugin.getListOfTranslatedCountries()))
            items.sort((a,b) => (a.text > b.text) ? 1 : (b.text > a.text) ? -1 : 0)
            return items
        },
        isLoading: {
            get() { return this.$store.getters.getIsLoading },
            set(isLoading) { this.$store.commit('setIsLoading', isLoading)}
        },
    },
    methods: {
        close() {
            this.showDialog = false
            this.releaseCostsplittingRows = []
            this.errorMessage = null
            this.$refs.costsplittingDialog.reset()
        },
        addNewRow() {
            // TODO: Validation is missing!
            this.$store.dispatch('addNewLineCostsplittingRows')

            // change error message, if share col is now over 100%
            let rows = this.releaseCostsplittingRows
            let shareSum =  parseFloat(rows.reduce((result, currentValue) => result + this.$alvPlugin.stringToNumber(currentValue.share), 0).toFixed(2))
            if(shareSum > 100) this.errorMessage = "lang_LABEL_RELEASES_COSTSPLITTING_ERROR_SHARE_NOT_100" 
            if(shareSum === 100) this.errorMessage = null
        },
        deleteRow(item) {
            this.$store.dispatch('deleteLineCostsplittingRows', item)
            // change error message, if share col is now under or exacly 100%
            let rows = this.releaseCostsplittingRows
            let shareSum =  parseFloat(rows.reduce((result, currentValue) => result + this.$alvPlugin.stringToNumber(currentValue.share), 0).toFixed(2))
            if(shareSum <= 100) {
                this.errorMessage = null
            }
        },
        splittingCosts() {
            // check if "share" column sum is exactly 100%
            let rows = this.releaseCostsplittingRows
            let shareSum =  parseFloat(rows.reduce((result, currentValue) => result + this.$alvPlugin.stringToNumber(currentValue.share), 0).toFixed(2))
            if(shareSum === 100) {
                this.errorMessage = null
                this.$store.dispatch('labelReleasesSplittingCosts')
                this.close()
            } else {
                this.errorMessage = "lang_LABEL_RELEASES_COSTSPLITTING_ERROR_SHARE_NOT_100"
            }
        },
        async calculateWithNewAmount() {
            let rows = this.releaseCostsplittingRows
            let amount = this.$store.getters.getLabelReleasesCostsplittingDetailsAmount
            if(amount !== null && amount !== '') {
                amount = JSON.parse(JSON.stringify(this.$alvPlugin.stringToFloat(amount)))
            } else amount === this.$n(0, "number")

            // Re-calculate pro rata amount for every row
            rows.forEach(row => {
                let proRataAmount = 0
                
                if(row.share !== null && row.share !== '') {
                    row.share = JSON.parse(JSON.stringify(this.$alvPlugin.stringToFloat(row.share)))
                } else row.share === this.$n(0, "number")

                let tempProRataAmount = amount*row.share
                if(tempProRataAmount !== 0) proRataAmount = tempProRataAmount/100

                row.proRataAmount = proRataAmount
            })
        },
    },
    created() {
        this.$store.commit('setCountries')
    },
}
</script>